import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ButtonComponent from '../../components/ButtonComponent';
import { AddressImg } from '../../assets/images';
import { Location } from '../../app-schemas/common';
import apiClient from '../../api-services/ApiClient';
import { SITELINK } from '../../app-routes/Links';

// ts-fn to handle type Guard
function isLocation(value: any): value is Location {
  return value && typeof value.latitude === 'number' && typeof value.longitude === 'number';
}

// main-component-ui
const NotificationSetup = () => {
  document.body.classList.remove('overflow-hidden');
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  // code to get user current location
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = 'Geolocation is not supported by this device.';
        setErrorMsg(error);
        return reject(new Error(error));
      }

      if ('permissions' in navigator && navigator.permissions.query) {
        navigator.permissions.query({ name: 'geolocation' }).then((status) => {
          if (status.state === 'denied') {
            const error =
              'Location access is blocked. Please enable it in your phone browser settings.';
            setErrorMsg(error);
            return reject(new Error(error));
          }

          // Safe to proceed now
          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve(position.coords);
            },
            (error) => {
              let message = '';
              switch (error.code) {
                case error.PERMISSION_DENIED:
                  message = 'User denied the request for Geolocation.';
                  break;
                case error.POSITION_UNAVAILABLE:
                  message = 'Location information is unavailable.';
                  break;
                case error.TIMEOUT:
                  message = 'The request to get user location timed out.';
                  break;
                default:
                  message = 'An unknown error occurred.';
              }

              setErrorMsg(`Error getting location: ${message}`);
              reject(new Error(message));
            },
            {
              enableHighAccuracy: true,
              timeout: 120000,
              maximumAge: 0,
            },
          );
        });
      } else {
        // Permissions API not available, just try geolocation
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve(position.coords);
          },
          (error) => {
            let message = '';
            switch (error.code) {
              case error.PERMISSION_DENIED:
                message = 'User denied the request for Geolocation.';
                break;
              case error.POSITION_UNAVAILABLE:
                message = 'Location information is unavailable.';
                break;
              case error.TIMEOUT:
                message = 'The request to get user location timed out.';
                break;
              default:
                message = 'An unknown error occurred.';
            }
            setErrorMsg(`Error getting location: ${message} ${JSON.stringify(error)}`);
            reject(new Error(message));
          },
          {
            enableHighAccuracy: true,
            timeout: 120000,
            maximumAge: 0,
          },
        );
      }
    });
  };

  const withTimeout = (promise: Promise<any>, timeout = 120000) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Geolocation timed out')), timeout),
      ),
    ]);

  const handleGeoLocation = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);

    let position;
    try {
      position = await withTimeout(getUserLocation());
    } catch (error) {
      setErrorMsg(
        error instanceof Error
          ? `Error getting your location: ${error.message}`
          : 'Unknown error getting your location.',
      );
      setLoading(false);
      return;
    }

    if (isLocation(position)) {
      const coordinates: Location = position;
      try {
        await apiClient.patch('/profile/updateGeolocation', coordinates);
        navigate(SITELINK.PROFILE_SETUP);
      } catch (error) {
        setErrorMsg('An error occurred connecting to the server. Please try again later.');
      } finally {
        setLoading(false);
      }
    } else {
      setErrorMsg(`Invalid location data received: ${JSON.stringify(position)}`);
    }
  };

  return (
    <section className="mt-8">
      {/* hero-text area */}
      <div className="flex flex-col items-start mt-2 px-6 pr-16">
        <h4 className="text-[20px] font-[400] text-black madimi-one flex gap-1 items-center">
          Enable Location Services to Confirm Account
        </h4>
      </div>

      {/* hero image */}
      <div className="flex flex-col items-center mt-12">
        <img src={AddressImg} alt="EmailCampaign" />
      </div>

      <div className="text-red-600 text-[13px] font-medium mt-3 col-span-full text-center px-6">
        {errorMsg}
      </div>

      <form onSubmit={handleGeoLocation} className="grid grid-cols-2 gap-3 mt-5">
        <div className={`fixed right-0 left-0 bottom-0 bg-white w-full col-span-full`}>
          <div className={`px-6 w-full`}>
            <ButtonComponent
              className="mb-5 w-[200px] mt-2 text-sm  py-3 rounded-lg text-white bg-custom-600"
              type="submit"
              disabled={loading}
              loading={loading}
            >
              Confirm
            </ButtonComponent>

            <p className="text-[10px] font-[400] text-center items-center mb-5 px-12">
              Click confirm grant location access to determine your country of residency
            </p>
          </div>
        </div>
      </form>
    </section>
  );
};

export default NotificationSetup;
